import { isNumber } from 'lodash';
import { useCallback, useMemo } from 'react';
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  APPLY_REGISTER_CLASS_ID,
  APPLY_REGISTER_ID,
  DATE_RANGE_SEARCH_PARAM_KEY,
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
  SUBSCRIBE_FROM_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useGetApplyRegisterName } from '@/modules/common/hooks/useGetApplyRegisterName.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import { useEntityItemUncachedQuery } from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { useMyActivitiesListQuery } from '@/modules/investor-dashboard/queries/activity.tsx';
import { useGetRegisterClassItem } from '@/modules/investor-dashboard/queries/registers.tsx';
import { useCreateShareholderMutation } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useCurrentEntityShareholdersQuery } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { investorDashboardPaths } from '@/modules/investor-dashboard/routes.tsx';
import {
  CheckSuccessIcon,
  LockGrayIcon,
  RedemptionIcon,
  SubscriptionIcon,
  TransfersIcon,
} from 'ui/icons';
import { ActivitiesType, EntityStatus, EntityType, EntityWithDocumentResponse } from 'api';

interface LockFunctional {
  icon: string;
  name: string;
  description: string;
}

export const itemsLockFunctional: LockFunctional[] = [
  {
    name: 'Subscriptions',
    description: 'You can apply for shares without KYC, but they will not be issued',
    icon: SubscriptionIcon,
  },
  {
    name: 'Transfers',
    description: 'You cannot make transfers if you are unverified',
    icon: TransfersIcon,
  },
  {
    name: 'Redemption',
    description: 'You cannot redeem shares or units until your account is fully verified',
    icon: RedemptionIcon,
  },
];

export const CompletionInformation = ({ allowSkip }: OnboardingStepProps) => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const step = params.get('step') ?? '0';
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));
  const apply_register_id = params.get(APPLY_REGISTER_ID);
  const apply_register_class_id = params.get(APPLY_REGISTER_CLASS_ID);

  const { mutateAsync: createShareholder } = useCreateShareholderMutation({
    onSuccess: data => {
      if (apply_register_class_id) {
        navigate(
          {
            pathname: entityPaths.static.subscribe + `/${data.shareholder.entity_id}`,
            search: params.toString(),
          },
          {
            replace: false,
          },
        );
      }
    },
  });

  const { data: dataTransactions } = useMyActivitiesListQuery(
    {
      page: 1,
      page_size: 1,
      type: ActivitiesType.Subscription,
      register_class_id: Number(apply_register_class_id),
    },
    Number(apply_register_id),
    !apply_register_id ||
      !apply_register_class_id ||
      !isNumber(+apply_register_class_id) ||
      !entity_id,
    Number(entity_id),
  );

  const { data: entityShareholdersResponse } = useCurrentEntityShareholdersQuery(
    Number(entity_id),
    !entity_id || +entity_id < 1,
  );
  const register_id = useMemo(
    () => (entityShareholdersResponse?.shareholders || [])?.[0]?.register_id || 'all',
    [entityShareholdersResponse?.shareholders],
  );

  const { isError: isErrorRegister, isLoading: isLoadingRegister } = useGetApplyRegisterName();
  const { isError: isErrorRegisterClass, isLoading: isLoadingRegisterClass } =
    useGetRegisterClassItem(
      Number(apply_register_class_id),
      !apply_register_class_id || isNaN(+apply_register_class_id),
    );

  const isSubscribed = useMemo(() => {
    if (
      isErrorRegister ||
      isLoadingRegister ||
      isErrorRegisterClass ||
      isLoadingRegisterClass ||
      !apply_register_id ||
      isNaN(+apply_register_id) ||
      !apply_register_class_id ||
      isNaN(+apply_register_class_id)
    )
      return true;

    return (dataTransactions?.pages?.[0]?.data?.length ?? 1) > 0; //default 1 because it's off Subscribe
  }, [
    apply_register_class_id,
    apply_register_id,
    dataTransactions?.pages,
    isErrorRegister,
    isErrorRegisterClass,
    isLoadingRegister,
    isLoadingRegisterClass,
  ]);

  const isShareholderExist = useMemo(() => {
    if (
      isErrorRegister ||
      isLoadingRegister ||
      isErrorRegisterClass ||
      isLoadingRegisterClass ||
      !apply_register_id ||
      isNaN(+apply_register_id)
    )
      return true;

    return !!(entityShareholdersResponse?.shareholders || []).find(
      item => item.register_id === Number(apply_register_id),
    );
  }, [
    apply_register_id,
    entityShareholdersResponse?.shareholders,
    isErrorRegister,
    isErrorRegisterClass,
    isLoadingRegister,
    isLoadingRegisterClass,
  ]);

  const isConfirmed = useMemo(() => {
    return !!data?.entity?.status && data?.entity?.status !== EntityStatus.EntityCreation;
  }, [data]);

  const isFilledRequiredField = useMemo(() => {
    return !!data?.entity?.entity_name;
  }, [data?.entity]);

  const handleCreateShareholder = useCallback(
    async (entity: EntityWithDocumentResponse) => {
      await createShareholder({ register_id: Number(apply_register_id), entity_id: entity.id });
    },
    [apply_register_id, createShareholder],
  );

  const handleGoDashboard = useCallback(() => {
    navigate(
      {
        pathname: investorDashboardPaths.static.dashboard,
        search: createSearchParams({
          [ENTITY_ID_SEARCH_PARAM_KEY]: String(data?.entity.id),
          [REGISTER_ID_SEARCH_PARAM_KEY]: String(register_id),
          [DATE_RANGE_SEARCH_PARAM_KEY]: 'all',
          ...(params.get('entity_id') === String(data?.entity.id)
            ? Object.fromEntries(params.entries())
            : {}),
          [APPLY_REGISTER_ID]: String(apply_register_id ?? ''),
          [APPLY_REGISTER_CLASS_ID]: String(apply_register_class_id ?? ''),
        }).toString(),
      },
      {
        replace: false,
      },
    );
  }, [apply_register_class_id, apply_register_id, data?.entity.id, navigate, params, register_id]);

  const handleContinue = useCallback(async () => {
    if (
      //Fixed: VES-1002: Rejected by BronID entity, can subscribe and go to the dashboard
      !(
        data?.entity?.status === EntityStatus.EntityCreation ||
        data?.entity?.status === EntityStatus.EntityAwaitingApproval ||
        data?.entity?.status === EntityStatus.EntityApproved
      )
    )
      return;

    if (!isFilledRequiredField) {
      return;
    }

    if (isSubscribed && !isNaN(+register_id)) {
      handleGoDashboard();
      return;
    }
    if (isSubscribed) {
      return;
    }
    params.set(SUBSCRIBE_FROM_PARAM_KEY, 'create');
    setParams(params, { replace: false });
    try {
      if (
        data?.entity &&
        !isShareholderExist &&
        !data?.entity?.entity_name?.toLowerCase()?.includes('pass')
      ) {
        await handleCreateShareholder(data?.entity);
      }
    } catch (e: any) {
      console.log('Error during creating Shareholder: ', e?.response?.data?.error_message ?? '');
    } finally {
      navigate(
        {
          pathname: entityPaths.static.subscribe + `/${entity_id}`,
          search: params.toString(),
        },
        {
          replace: false,
        },
      );
    }
  }, [
    isFilledRequiredField,
    isSubscribed,
    register_id,
    params,
    setParams,
    handleGoDashboard,
    data?.entity,
    isShareholderExist,
    handleCreateShareholder,
    navigate,
    entity_id,
  ]);

  const handleBackStep = () => {
    const newStep = Number(step) - 1;

    params.set('step', String(newStep));
    setParams(params, { replace: false });
  };

  const hintContinue = useMemo(() => {
    let field = '';
    switch (data?.entity?.type) {
      case EntityType.Individual: {
        field = 'Contact name';
        break;
      }
      case EntityType.Company: {
        field = 'Company name';
        break;
      }
      case EntityType.Trust: {
        field = 'Trust name';
        break;
      }
      case EntityType.Smsf: {
        field = 'SMSF name';
        break;
      }
      case EntityType.Association: {
        field = 'Association name';
        break;
      }
      case EntityType.Partnership: {
        field = 'Partnership name';
        break;
      }
    }
    return `You can't continue until you provide the ${field}`;
  }, [data?.entity]);

  return (
    <OnboardingWrapper
      onClickContinue={
        data?.entity?.status === EntityStatus.EntityCreation ||
        data?.entity?.status === EntityStatus.EntityAwaitingApproval ||
        data?.entity?.status === EntityStatus.EntityApproved
          ? handleContinue
          : undefined
      }
      buttonContinueHint={
        !isFilledRequiredField
          ? hintContinue
          : isSubscribed && isNaN(+register_id)
          ? `You can't continue without an invitation to the registry`
          : undefined
      }
      onClickSkip={
        allowSkip && data?.entity?.status === EntityStatus.EntityCreation
          ? handleBackStep
          : undefined
      }
      disableNext={!isFilledRequiredField || (isSubscribed && isNaN(+register_id))}
      buttonSkipName="Back to KYC"
      className="max-w-[560px]"
    >
      <div className="w-full flex flex-col justify-center items-center text-center gap-4">
        <span>
          <img src={CheckSuccessIcon} alt="success" className="w-[96px] h-[96px]" />
        </span>
        <span className="leading-[56px] text-5xl font-[600] font-[RecifeDisplay]">
          Entity created successfully!
        </span>
        <span className="text-[16px] leading-6 text-gray-900">
          {isConfirmed
            ? 'and verification is in progress'
            : 'Congratulations! Your entity has successfully been created but not verified yet'}
        </span>
        <span className="text-[16px] leading-6 text-gray-900">
          {isConfirmed
            ? `Your entity has been created, and we are verifying it, we will email you once it's done.
          Before completing verification, your limitations will be:`
            : `Complete the verification process to access all features and functionality within Compli One.
             Before completing verification, your limitations will be:`}
        </span>
        <div className="flex flex-col gap-4 w-full">
          {itemsLockFunctional.map(item => (
            <div className="flex gap-3" key={item.name}>
              <span className="relative flex items-center justify-center">
                <span className="p-4 rounded-full bg-gray-200">
                  <img
                    src={item.icon}
                    alt="Subscription Icon"
                    className="w-6 h-6 min-w-6 min-h-6"
                  />
                </span>
                <span className="absolute bottom-[-2px] right-[-2px]">
                  <img src={LockGrayIcon} alt="lock" className="w-6 h-6 min-w-6 min-h-6" />
                </span>
              </span>
              <span className="flex flex-col items-start gap-1 text-left">
                <span className="text-[16px] leading-6 text-gray-900">{item.name}</span>
                <span className="text-sm leading-[22px] text-[#6B6B6B]">{item.description}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </OnboardingWrapper>
  );
};
